<template>
  <div class="controls">
    <filter-panel-new
      @update="applyFilter"
      direction="vertical"
      :filter-groups="formattedFilterGroups"
      :apply-filter-immediately="false"
      :no-paddings="true"
      :filters-icon-enabled="false"
    />
  </div>
</template>

<script>
import getNested from 'lodash-es/get';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';

import FilterPanelNew, {
  applyFilterValues,
  getCurrentFilterValues,
  getCurrentAPIFilterValues,
} from '@/components/widgets/FilterPanelNew';

export default {
  name: 'live-map-filters',

  components: {
    FilterPanelNew,
  },

  data() {
    return {
      filtersReady: false,
      defaultFilterValues: null,

      filterSchema: {
        destinationRegion: {
          name: 'destinationRegion',
          apiName: 'destinationRegion',
          type: 'multiselect-region',
          placeholder: 'Destination Region',
          value: [],
          defaultChoice: null,
          choices: [],
        },

        includeUnknownDestinationRegion: {
          name: 'includeUnknownDestinationRegion',
          apiName: 'includeUnknownDestinationRegion',
          type: 'checkbox',
          label: 'Include unknown destination region',
          value: true,
          defaultChoice: true,
        },

        enableEtaFilter: {
          name: 'enableEtaFilter',
          apiName: 'enableEtaFilter',
          type: 'switch',
          label: 'Enable ETA filter',
          value: false,
          defaultChoice: false,
        },

        arrivalDate: {
          name: ['arrivalDateStart', 'arrivalDateEnd'],
          type: 'daterange-picker',
          options: {
            ranges: this.getDatePickerRanges(),
          },
          label: 'ETA: ',

          arrivalDateStart: {
            name: 'arrivalDateStart',
            apiName: 'arrivalDateStart',
            value: moment()
              .utc()
              .format('YYYY-MM-DD'),
          },
          arrivalDateEnd: {
            name: 'arrivalDateEnd',
            apiName: 'arrivalDateEnd',
            value: moment()
              .utc()
              .add(120, 'days')
              .format('YYYY-MM-DD'),
          },
        },

        // sliders: dwt, speed, ladenPercent
        dwt: {
          type: 'range',
          name: 'dwt',
          apiName: 'dwt',
          label: 'DWT',
          min: 0,
          max: 450000,
          value: [0, 450000],
        },
        speed: {
          type: 'range',
          name: 'speed',
          apiName: 'speed',
          label: 'Speed',
          min: 0,
          max: 25,
          value: [0, 25],
        },
        ladenStatus: {
          name: 'ladenStatus',
          apiName: 'ladenStatus',
          type: 'multiselect',
          placeholder: 'Laden status',

          value: [],
          choices: [
            {text: 'Laden', value: 'Laden'},
            {text: 'Ballast', value: 'Ballast'},
          ],
          defaultChoice: [],
        },

        ladenPercent: {
          type: 'range',
          name: 'ladenPercent',
          apiName: 'ladenPercent',
          label: 'Laden %',
          min: 0,
          max: 100,
          value: [0, 100],
        },

        excludeMpv: {
          name: 'excludeMpv',
          apiName: 'excludeMpv',
          type: 'checkbox',
          label: 'Exclude Multi Purpose Vessels',
          value: true,
          defaultChoice: true,
        },

        commodity: {
          name: 'commodity',
          apiName: 'commodity',
          type: 'treeselect',
          placeholder: 'Commodity',
          value: [],
          choices: [],
          rawChoices: [],
          defaultChoice: [],
          options: {
            toggleAll: true,
          },
        },

        subSegment: {
          name: 'subSegment',
          apiName: 'subSegment',
          type: 'treeselect',
          placeholder: 'Segment',
          value: [],
          choices: [],
          rawChoices: [],
          defaultChoice: [],
          options: {
            toggleAll: true,
          },
        },
      },
    };
  },

  methods: {
    ...mapActions('api/tradeflows', [
      'getTradeflowsRegionsWithPolygons',
      'getCommodities',
      'getSegments',
    ]),

    async loadFilters() {
      await this.loadCommodityChoices();
      await this.loadSegmentChoices();
      this.filtersReady = true;
    },

    async loadSegmentChoices() {
      const response = await this.getSegments({group: true});
      this.filterSchema.subSegment.choices = response || [];
    },

    async loadCommodityChoices() {
      this.filterSchema.commodity.choices = await this.getCommodities({
        group: true,
      });
    },

    applyFilter(filterValues) {
      if(!this.filtersReady) {
        return;
      }

      applyFilterValues(this.filterSchema, filterValues);
      this.$emit('update', getCurrentAPIFilterValues(this.filterSchema));
    },

    getDatePickerRanges() {
      return {
        Today: [this.getToday().toDate(), this.getToday().toDate()],
        'Next 7 days': [
          this.getToday().toDate(),
          this.getToday()
            .add(7, 'days')
            .toDate(),
        ],
        'Next 14 days': [
          this.getToday().toDate(),
          this.getToday()
            .add(14, 'days')
            .toDate(),
        ],
        'Next 30 days': [
          this.getToday().toDate(),
          this.getToday()
            .add(30, 'days')
            .toDate(),
        ],
        'Next 3 months': [
          this.getToday().toDate(),
          this.getToday()
            .add(3, 'months')
            .toDate(),
        ],
        'Next 6 months': [
          this.getToday().toDate(),
          this.getToday()
            .add(6, 'months')
            .toDate(),
        ],
      };
    },

    getToday() {
      return moment()
        .utc()
        .set({
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        });
    },

    async applyFilterPreset() {
      const filterValues = getNested(
        this.getActivePreset,
        'params.filterPanel',
        {},
      );

      this.filtersReady = true;
      applyFilterValues(this.filterSchema, filterValues);
    },

    async loadDefaultFilterValues() {
      await this.loadFieldChoices();
      this.defaultFilterValues = getCurrentFilterValues(this.filterSchema);
      this.filtersReady = true;

      this.applyFilter(this.defaultFilterValues);
    },

    async loadFieldChoices() {
      await this.loadFilters();
      this.filterSchema.destinationRegion.choices = await this.getTradeflowsRegionsWithPolygons(
        {direction: 'import'},
      );
    },
  },

  computed: {
    ...mapGetters('api/presets', ['getActivePreset']),

    formattedFilterGroups() {
      return [
        {
          title: 'Region and date',
          items: [
            this.filterSchema.destinationRegion,
            this.filterSchema.includeUnknownDestinationRegion,
            this.filterSchema.enableEtaFilter,
            this.filterSchema.arrivalDate,
          ],
        },
        {
          title: ' ',
          items: [
            this.filterSchema.dwt,
            this.filterSchema.subSegment,
            this.filterSchema.commodity,
          ],
        },
        {
          title: ' ',
          items: [
            this.filterSchema.speed,
            this.filterSchema.ladenStatus,
            this.filterSchema.ladenPercent,
            this.filterSchema.excludeMpv,
          ],
        },
      ];
    },
  },

  watch: {
    getActivePreset: {
      immediate: true,
      handler: async function (preset) {
        if(preset && this.$route.path !== getNested(preset, 'viewName')) {
          // prevent preset loading on a view change,
          // it's possible to load wrong preset otherwise
          return;
        }

        if(!this.filtersReady && !preset) {
          await this.loadDefaultFilterValues();
        }

        // filter panel will be updated by props values and .applyFilter() will be emitted
        if(preset) {
          await this.applyFilterPreset(preset);
        }
      },
    },
  },
};
</script>
